import React from "react";
import { DiscussionWithoutContentDTO, GroupDTO, HomeDiscussionFeedPageDTO, SeriesDTO, UserProfileDTO } from "@/generated-client";
import { CarouselItem } from "@/components/ui/carousel";
import { ClientFactory, type HomeGroupsFeedPage, type HomeViewerGraphTier } from "@/main/common/ClientFactory";
import { DiscussionCardDisplayer } from "@/main/react/card/DiscussionCardDisplayer";
import { AdSlot } from "@/main/react/ads/AdSlot";
import { areAdsAllowed } from "@/main/analytics/ads/googlePublisherTag";
import { WarningBanner } from "@/main/react/component/WarningBanner";
import { InfoBanner } from "@/main/react/component/InfoBanner";
import { Localizer } from "@/main/react/Types";
import { MAIN_PAGE_LIST_WIDTH_CLASS, MainPageListStack } from "@/main/react/list/MainPageListLayout";
import { createStandardPaginatedStatusSlots, PaginatedListEmptyState } from "@/main/react/list/PaginatedListState";
import { crawlableDiscussionListFallback } from "@/main/react/list/ssrFallbacks";
import { DiscussionCardListSkeleton } from "@/main/react/list/PaginatedListSkeletons";
import { ShortPostComposer } from "@/main/react/shortpost/ShortPostComposer";
import { PaginatedListWrapperV2 } from "@/main/react/list/PaginatedListWrapperV2";
import { isInterruptedRequestError } from "@/main/common/ApiUtils";
import { navigateToUrl } from "@/main/common/BrowserNavigation";
import { CalendarRange, Compass, FilterX, History, List, MessageSquareText, RefreshCw, UserPlus, UserSquare2, Users, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import logger from "@/lib/logger";
import { reportFeedPageShown } from "@/main/analytics/feedAttribution";
import type { AnalyticsDiscoveryTarget, AnalyticsFeedOrdering, AnalyticsFeedType } from "@/main/analytics/analyticsEvents";
import { useDiscoveryRailImpression } from "@/main/react/hooks/useDiscoveryRailImpression";
import { HomeFeedType, homeFeedTypeSupportsSort, homeOrderingSupportsRange, HomeOrderingStyle, HomeRange, LoadableItems } from "./HomePageShared";
import { nextWiderRange } from "./homeRangeAutoNarrow";
import { MetaHandler } from "@/main/common/MetaHandler";
import { ActivePublisherCard, RecommendedGroupCard, RecommendedSeriesCard } from "./HomeDiscoveryRail";
import { HomeMobileDiscoveryRailSection } from "./HomeMobileDiscoveryRailSection";
import { RecentlyViewedDiscussionCard } from "@/main/react/page/reading-history/RecentlyViewedDiscussionRail";

const HOME_DISCUSSION_PAGE_SIZE = 10;
const HOME_INLINE_DISCOVERY_INTERVAL = 5;
/**
 * `HOME-FEED-8` (depth half) and `LANDING-49`, wave-12 sheet Q5 option A: clamped 12 -> 10 so it MATCHES
 * `HOME_DISCUSSION_PAGE_SIZE`.
 *
 * <p>A discovery row is emitted only on a COMPLETE chunk (`page.length === interval`). At 12 against a
 * 10-row fetch page no chunk could ever be complete, so the interleaved discovery layer was unreachable
 * on every non-`home` feed at any scroll depth, anonymous and signed-in alike — the layer was not merely
 * late, it never arrived. These two constants are load-bearing on each other: changing the page size
 * without changing this one silently switches the layer off again.
 */
const DISCOVERY_INLINE_DISCOVERY_INTERVAL = 10;

type InlineDiscoveryRow = {
    key: "groups" | "users" | "series" | "continue" | "yourGroups";
    /** What the row recommends — the `target_type` on both `discovery_rail_shown` and its clicks. */
    targetType: AnalyticsDiscoveryTarget;
    icon: LucideIcon;
    title: string;
    subtitle: string;
    state:
        | LoadableItems<GroupDTO>
        | LoadableItems<SeriesDTO>
        | LoadableItems<UserProfileDTO>
        | LoadableItems<DiscussionWithoutContentDTO>;
    error: string | null;
    cards: React.ReactNode[];
    playwrightId: string;
    /** `HOME-FEED-27` — false for the membership module; see `HomeInlineDiscoveryRow`. */
    reportImpression?: boolean;
};

function hasVisibleDiscoveryState<T>(state: LoadableItems<T>): boolean {
    return state.loading || Boolean(state.error) || state.items.length > 0;
}

/** The STEADY-STATE cadence: how many cards sit between two discovery rows, from the second row on. */
function getInlineDiscoveryInterval(feedType: HomeFeedType): number {
    return feedType === "home" ? HOME_INLINE_DISCOVERY_INTERVAL : DISCOVERY_INLINE_DISCOVERY_INTERVAL;
}

/**
 * `HOME-FEED-9` — how many cards precede the FIRST discovery row, which is the only number that row
 * measures. Everything after the first row keeps `getInlineDiscoveryInterval` untouched.
 *
 * <p><b>Why a lead and not a smaller interval.</b> The row's Direction is "one module near the top when
 * the graph is empty, then the existing low-interruption cadence". Shrinking the interval outright
 * would interrupt an established member's whole scroll to fix the first screen of a new member's; a
 * lead moves exactly the one boundary the four measurements are about (2.5 to 6.2 phone screens deep,
 * `?feed=all&ordering=top&range=day` being the worst) and changes nothing below it.
 *
 * <p><b>Constraint 1, the LCP element, is satisfied by construction.</b> This is a count of cards
 * BEFORE the row, so the minimum it can ever produce is "after card 1". Nothing is lifted above the
 * first feed card and no height is reserved above it, which is what the CWV work protects.
 *
 * <p><b>Constraint 2, the deliberately wider `all`/`groups` interval, is preserved as an ordering</b>
 * rather than discarded: a discovery corpus needs less discovery injection, so those feeds keep a
 * strictly larger lead than `home` at every tier, and `Math.min` guarantees a lead never exceeds the
 * feed's own cadence.
 *
 * <p><b>`ESTABLISHED`, and an absent tier, change nothing.</b> An absent tier is an anonymous visitor
 * (`viewerGraph` is null for them, and `LANDING-49` owns that half), a server that predates the field,
 * or a bootstrap still in flight. All three must read as today's behaviour, never as an empty graph.
 */
const INLINE_DISCOVERY_LEAD_BY_TIER: Record<"EMPTY" | "SPARSE", { home: number; other: number }> = {
    EMPTY: { home: 3, other: 5 },
    SPARSE: { home: 4, other: 7 },
};

function getInlineDiscoveryLead(feedType: HomeFeedType, viewerGraphTier?: HomeViewerGraphTier | null): number {
    const interval = getInlineDiscoveryInterval(feedType);
    if (viewerGraphTier !== "EMPTY" && viewerGraphTier !== "SPARSE") {
        return interval;
    }

    const lead = INLINE_DISCOVERY_LEAD_BY_TIER[viewerGraphTier];
    return Math.min(feedType === "home" ? lead.home : lead.other, interval);
}

/**
 * `HOME-FEED-17` — the width cap on one mobile discovery slide.
 *
 * <p>Was `max-w-[19rem]` (304px) on the three recommendation rows and `max-w-[20rem]` (320px) on the
 * continue-reading row. That cap is the SECOND thing narrowing the slide, and it is the one the
 * register missed: with the old 72px arrow gutter the carousel viewport was ~276px at 412 CSS px, so
 * the cap never bound and the frame measured 275px. Drop the gutter alone and the slide stops at 304px
 * rather than filling the module, recovering 29 of the 73 lost pixels and leaving the row to re-file
 * itself.
 *
 * <p>22rem (352px) is chosen so the cap does NOT bind on a phone — a 412px viewport leaves ~316px of
 * module content once the coarse-pointer inset is taken — while still stopping a single card from
 * stretching across a 1023px tablet, which is also below `lg` and also renders this rail.
 */
const INLINE_DISCOVERY_SLIDE_CLASS = "mx-auto flex h-full w-full max-w-[22rem]";

/**
 * One client-side chunk of the feed. It carries its own `startIndex` and `expectedSize` because the
 * FIRST chunk can now be shorter than the rest (`HOME-FEED-9`'s lead), so neither the boundary test
 * nor the analytics position can be derived from `pageIndex * interval` any more.
 */
type FeedChunk<T> = {
    items: T[];
    /** Absolute index of this chunk's first item in the flat feed, for `feedAttribution.position`. */
    startIndex: number;
    /** How many items a COMPLETE chunk holds here. A boundary is emitted only on a complete chunk. */
    expectedSize: number;
};

function chunkFeedItems<T>(items: T[], leadSize: number, interval: number): FeedChunk<T>[] {
    if (items.length === 0) {
        return [];
    }

    const safeInterval = interval > 0 ? interval : items.length;
    const safeLead = leadSize > 0 ? leadSize : safeInterval;

    const chunks: FeedChunk<T>[] = [];
    let index = 0;
    while (index < items.length) {
        const expectedSize = chunks.length === 0 ? safeLead : safeInterval;
        chunks.push({ items: items.slice(index, index + expectedSize), startIndex: index, expectedSize });
        index += expectedSize;
    }

    return chunks;
}

/**
 * What the feed is ACTUALLY sorted by, which is not always what the ordering picker holds.
 *
 * <p>The recommended home feed ignores `orderingStyle` entirely (it is ranked), and the board lane is
 * server-ordered. Emitting the picker's value for either would put a dimension in GA4 that describes
 * nothing. The three scopes are sortable since HS-3a and their fetch sends `orderingStyle` verbatim,
 * so for them the picker's value IS the sort.
 */
function resolveHomeFeedOrdering(feedType: HomeFeedType, orderingStyle: HomeOrderingStyle): AnalyticsFeedOrdering {
    if (feedType === "home") {
        return "recommended";
    }
    // The board lane's order is the SERVER's (A2) and is not the placeholder the selector carries.
    if (feedType === "boards") {
        return "closing-soonest";
    }
    return orderingStyle;
}

/**
 * Round 37 `2a` (FI-587): the page's `newReplyCounts` map (post public id to replies since the viewer last read it)
 * rides each card as `newRepliesSinceLastVisit`, which `DiscussionCardDisplayer` renders as "N new replies". Absent
 * from the map, absent on the card.
 */
function newRepliesFor(newReplyCounts: Record<string, number> | undefined, publicId: string | undefined): { newRepliesSinceLastVisit?: number } {
    const count = publicId ? newReplyCounts?.[publicId] : undefined;
    return typeof count === "number" && count > 0 ? { newRepliesSinceLastVisit: count } : {};
}

function toHomeFeedListItems(feedPage?: HomeDiscussionFeedPageDTO | null): DiscussionWithoutContentDTO[] {
    const items = feedPage?.items ?? [];
    const nextCursor = feedPage?.nextCursor ?? undefined;

    return items.map((discussion, index) => ({
        ...discussion,
        ...newRepliesFor(feedPage?.newReplyCounts, discussion.publicId),
        paginationParameter: index === items.length - 1 ? nextCursor : undefined,
    }));
}

function toHomeGroupsFeedItems(feedPage?: HomeGroupsFeedPage | null): DiscussionWithoutContentDTO[] {
    const items = feedPage?.items ?? [];
    const nextCursor = feedPage?.nextCursor ?? undefined;

    return items.map((discussion, index) => ({
        ...discussion,
        ...newRepliesFor(feedPage?.newReplyCounts, discussion.publicId),
        paginationParameter: index === items.length - 1 ? (nextCursor ?? undefined) : undefined,
    }));
}

function HomeInlineDiscoveryRow(props: Omit<InlineDiscoveryRow, "key"> & { localizer: Localizer; onRetry?: () => void }) {
    const { targetType, icon, title, subtitle, state, error, cards, playwrightId, reportImpression = true, localizer, onRetry } = props;
    const loading = state.loading;

    // Before the early return: hooks must run unconditionally. The mobile rows are not an accordion —
    // a rendered row is an open row — so readiness is just "resolved and non-empty".
    //
    // `reportImpression` is false for exactly one row, the `HOME-FEED-27` My-groups module: it is a
    // membership surface, not a recommendation, and counting its impressions here would move the
    // denominator of `discovery_rail_shown{target_type=group, surface=mobile_inline}` without any of
    // those impressions being a recommendation. The cards suppress the click half symmetrically
    // (`RecommendedGroupCard` in `membership` mode emits no `targetType`), so the instrument stays
    // internally consistent rather than half-counted.
    useDiscoveryRailImpression({
        targetType,
        surface: "mobile_inline",
        itemCount: cards.length,
        ready: reportImpression && !loading && !error,
    });

    if (!loading && !error && cards.length === 0) {
        return null;
    }

    // Mirror the desktop rail's error affordance (HomeDiscoveryRail's HomeRailSection): a warning
    // banner plus a retry CTA so a transient discovery failure isn't a dead end on mobile.
    const errorNode = error ? (
        <div className="flex flex-col items-start gap-2">
            <WarningBanner description={error} className="py-3" />
            {onRetry ? (
                <CtaButton
                    icon={RefreshCw}
                    text={localizer("literal.retry")}
                    size="sm"
                    variant="outline"
                    onClick={onRetry}
                />
            ) : null}
        </div>
    ) : undefined;

    return (
        <HomeMobileDiscoveryRailSection
            icon={icon}
            title={title}
            subtitle={subtitle}
            cards={cards}
            loading={loading}
            loadingLabel={localizer("home.loading")}
            errorNode={errorNode}
            sectionPlaywrightId={playwrightId}
            carouselPlaywrightId={`${playwrightId}-carousel`}
            // `HOME-FEED-18`. `pagination.previous` / `pagination.next` already exist in all 25
            // catalogs, so the arrows' accessible names cost no new key. Without these the only name
            // either arrow has is the hardcoded English `sr-only` literal inside `components/ui`.
            previousLabel={localizer("pagination.previous")}
            nextLabel={localizer("pagination.next")}
        />
    );
}

export function HomeFeedList(props: {
    localizer: Localizer;
    feedType: HomeFeedType;
    orderingStyle: HomeOrderingStyle;
    range: HomeRange;
    // CWV L1: server-inlined first feed page. When present, PaginatedListWrapperV2 seeds from it and
    // skips the mount-time fetch; load-more still paginates from the last item's embedded cursor.
    initialFeedItems?: DiscussionWithoutContentDTO[];
    groupsState: LoadableItems<GroupDTO>;
    seriesState: LoadableItems<SeriesDTO>;
    usersState: LoadableItems<UserProfileDTO>;
    historyState: LoadableItems<DiscussionWithoutContentDTO>;
    /**
     * `HOME-FEED-27` (operator decision **D25**) — the member's OWN groups, loaded by `HomePage` and
     * only on the `groups` feed. Optional: absent means today's behaviour everywhere.
     */
    yourGroupsState?: LoadableItems<GroupDTO>;
    /**
     * `HOME-FEED-9` and `HOME-FEED-10` — how much personalisation signal this viewer has given us,
     * read off the discovery bootstrap `HomePage` already fetches once per mount.
     *
     * <p>Optional and null-tolerant on purpose: absent means anonymous, or a server without the field,
     * or a bootstrap still in flight, and all three must render exactly as this component did before
     * the signal existed.
     */
    viewerGraphTier?: HomeViewerGraphTier | null;
    onFeedTypeChange?: (value: HomeFeedType) => void;
    // `HOME-FEED-11`. Clears the time range from the filtered-to-zero empty state. Wired to the same
    // handler the range pill uses, so widening the window from the empty panel goes through the same
    // state normalization and URL write as widening it from the control.
    onRangeChange?: (value: HomeRange) => void;
    // Reloads the discovery bootstrap (groups/series/publishers/history); wired to the retry CTA in
    // each mobile inline discovery row so a failed rail can recover without a hard page refresh.
    onRetry?: () => void;
}) {
    const { localizer, feedType, orderingStyle, range, initialFeedItems, groupsState, seriesState, usersState, historyState, yourGroupsState, viewerGraphTier, onFeedTypeChange, onRangeChange, onRetry } = props;
    const paginationApi = React.useMemo(() => ClientFactory.getAjaxPaginationRestControllerApi(), []);
    const nativeResumeNewPosts = React.useMemo(
        () => ({ newItemsLabel: localizer("feed.resume.new_posts") }),
        [localizer],
    );
    const inlineDiscoveryInterval = React.useMemo(() => getInlineDiscoveryInterval(feedType), [feedType]);
    const inlineDiscoveryLead = React.useMemo(
        () => getInlineDiscoveryLead(feedType, viewerGraphTier),
        [feedType, viewerGraphTier],
    );
    const inlineDiscoveryRows = React.useMemo<InlineDiscoveryRow[]>(() => {
        const rows: InlineDiscoveryRow[] = [
            {
                key: "groups",
                targetType: "group",
                icon: Users,
                title: localizer("home.groups.title"),
                subtitle: localizer("home.groups.subtitle"),
                state: groupsState,
                error: groupsState.error,
                cards: groupsState.items.map((group) => (
                    <CarouselItem
                        key={group.publicId ?? group.slug ?? group.name ?? "group"}
                        className="flex basis-full pl-0"
                    >
                        <div className={INLINE_DISCOVERY_SLIDE_CLASS}>
                            <RecommendedGroupCard group={group} localizer={localizer} layout="inline" />
                        </div>
                    </CarouselItem>
                )),
                playwrightId: "home-inline-groups-row",
            },
            {
                key: "users",
                targetType: "publisher",
                icon: UserSquare2,
                title: localizer("home.publishers.title"),
                subtitle: localizer("home.publishers.subtitle"),
                state: usersState,
                error: usersState.error,
                cards: usersState.items.map((user) => (
                    <CarouselItem
                        key={user.username ?? "user"}
                        className="flex basis-full pl-0"
                    >
                        <div className={INLINE_DISCOVERY_SLIDE_CLASS}>
                            <ActivePublisherCard user={user} localizer={localizer} layout="inline" />
                        </div>
                    </CarouselItem>
                )),
                playwrightId: "home-inline-publishers-row",
            },
            {
                key: "series",
                targetType: "series",
                icon: List,
                title: localizer("home.series.title"),
                subtitle: localizer("home.series.subtitle"),
                state: seriesState,
                error: seriesState.error,
                cards: seriesState.items.map((series, index) => (
                    <CarouselItem
                        key={series.publicId ?? series.slug ?? series.title ?? `series-${index}`}
                        className="flex basis-full pl-0"
                    >
                        <div className={INLINE_DISCOVERY_SLIDE_CLASS}>
                            <RecommendedSeriesCard series={series} localizer={localizer} layout="inline" />
                        </div>
                    </CarouselItem>
                )),
                playwrightId: "home-inline-series-row",
            },
            {
                key: "continue",
                targetType: "discussion",
                icon: History,
                title: localizer("home.continue_reading.title"),
                subtitle: localizer("home.continue_reading.subtitle"),
                state: historyState,
                // Continue-reading is a bonus resume surface: never reserve a slot for a
                // loading/error placeholder. It only joins the rotation once real history exists.
                error: null,
                cards: historyState.items.map((discussion, index) => (
                    <CarouselItem
                        key={discussion.publicId ?? discussion.slug ?? `recent-${index}`}
                        className="flex basis-full pl-0"
                    >
                        <div className={INLINE_DISCOVERY_SLIDE_CLASS}>
                            <RecentlyViewedDiscussionCard
                                discussion={discussion}
                                localizer={localizer}
                                itemPlaywrightId="home-continue-reading-card"
                                analyticsSurface="mobile_home"
                            />
                        </div>
                    </CarouselItem>
                )),
                playwrightId: "home-inline-continue-reading-row",
            },
        ];

        const visible = rows.filter((row) => {
            switch (row.key) {
                case "groups":
                    return hasVisibleDiscoveryState(groupsState);
                case "users":
                    return hasVisibleDiscoveryState(usersState);
                case "series":
                    return hasVisibleDiscoveryState(seriesState);
                case "continue":
                    return historyState.items.length > 0;
            }
        });

        // `HOME-FEED-45` (operator requirement, 2026-09-24) — a FIXED order led by groups: groups,
        // people to follow, series, continue reading. The literal above IS the order; this only
        // filters. Standing principle (designs/implementation-map/discovery.md): groups get priority
        // for space in home discovery.
        //
        // This deliberately reverses two earlier decisions, so do not re-introduce either:
        //  - `HOME-FEED-12`'s hoist, which put "continue" first whenever the member had history;
        //  - Tier-2 finding 30's per-(viewer, day) rotation of the lead (`homeRotation.ts`, deleted 2026-09-24), which
        //    handed the first boundary to a different module on most days.
        //
        // "continue" is still gated on `items.length`, never on a visible/loading flag: it never
        // reserves a slot for an in-flight or failed history request.
        //
        // ⚠️ MEASUREMENT: this moves `discovery_card_clicked{surface=mobile_home}`'s denominator on
        // 2026-09-24. Recorded in designs/01-now/2026-08-07-recommendation-surface-measurement-design.md
        // §"Measurement discontinuities"; do not read a window straddling it as a change in behaviour.
        return visible;
    }, [groupsState, historyState, localizer, seriesState, usersState]);

    /**
     * `HOME-FEED-27` (operator decision **D25**) — the member's own groups, for the TERMINAL slot only.
     *
     * <p>D25: "it is the member's view of their groups: show the groups themselves when the post list
     * is thin", and it names the slot explicitly as the one `HOME-FEED-8` and `HOME-FEED-19` both
     * want. So this is deliberately NOT another entry in `inlineDiscoveryRows`:
     *
     * <ul>
     *   <li><b>Not in the rotation.</b> Joining it would interrupt a long, healthy groups feed with a
     *       list of groups the member is already in — which is noise, not discovery — and it would
     *       also shift the rotation's modulus, moving the denominator of every inline discovery
     *       impression on this feed for a reason that has nothing to do with discovery.</li>
     *   <li><b>One slot, one treatment.</b> The terminal branch picks this OR the rotation head, never
     *       both, so the "must not stack" constraint `HOME-FEED-19` records is preserved by
     *       construction rather than by a second gate someone can forget.</li>
     *   <li><b>No impression, no click event.</b> See `HomeInlineDiscoveryRow` and
     *       `RecommendedGroupCard`'s `membership` mode.</li>
     * </ul>
     */
    const yourGroupsRow = React.useMemo<InlineDiscoveryRow | null>(() => {
        const groups = yourGroupsState?.items ?? [];
        if (feedType !== "groups" || groups.length === 0) {
            return null;
        }

        return {
            key: "yourGroups",
            targetType: "group",
            icon: Users,
            title: localizer("sidebar.groups"),
            subtitle: localizer("home.your-groups.subtitle"),
            state: yourGroupsState ?? { items: [], loading: false, error: null },
            // Never an error placeholder, for the same reason the continue-reading row never carries
            // one: this is a bonus surface that appears below an exhausted list, and a failed
            // best-effort fetch must leave the page exactly as it was rather than print a warning.
            error: null,
            cards: groups.map((group, index) => (
                <CarouselItem
                    key={group.publicId ?? group.slug ?? group.name ?? `your-group-${index}`}
                    className="flex basis-full pl-0"
                >
                    <div className={INLINE_DISCOVERY_SLIDE_CLASS}>
                        <RecommendedGroupCard group={group} localizer={localizer} layout="inline" mode="membership" />
                    </div>
                </CarouselItem>
            )),
            playwrightId: "home-inline-your-groups-row",
            reportImpression: false,
        };
    }, [feedType, localizer, yourGroupsState]);

    const fetchPage = React.useCallback(async (paginationParameter?: string | number): Promise<DiscussionWithoutContentDTO[]> => {
        try {
            if (feedType === "all") {
                if (orderingStyle === "active") {
                    const cursor = typeof paginationParameter === "string" ? paginationParameter : undefined;
                    const response = await paginationApi.activeDiscussionsPage(cursor);
                    return response?.data ?? [];
                }

                if (orderingStyle === "new") {
                    const cutoffCreationDate = typeof paginationParameter === "string" ? paginationParameter : undefined;
                    const response = await paginationApi.newDiscussionsPage(cutoffCreationDate);
                    return response?.data ?? [];
                }

                const cursor = paginationParameter == null ? undefined : String(paginationParameter);

                if (orderingStyle === "controversial") {
                    const response = await ClientFactory.getMostControversialDiscussionsPage(range, cursor);
                    return response?.data ?? [];
                }

                if (orderingStyle === "arguments") {
                    const response = await ClientFactory.getMostArguedDiscussionsPage(range, cursor);
                    return response?.data ?? [];
                }

                const response = await ClientFactory.getMostAgreedDiscussionsPage(range, cursor);
                return response?.data ?? [];
            }

            if (feedType === "unanswered") {
                // Same recency-cursor contract as the "new" lane — this is that feed with the
                // unanswered-question predicate applied server-side.
                const cutoffCreationDate = typeof paginationParameter === "string" ? paginationParameter : undefined;
                const response = await ClientFactory.getUnansweredPostsPage(cutoffCreationDate);
                return response?.data ?? [];
            }

            if (feedType === "boards") {
                // "Boards to vote on" (board pace design §10). The cursor is the previous page's last
                // `opened_at` and it walks FORWARD — the lane orders ASCENDING, closest to its window
                // expiring first — which is why the wrapper's parameter is `cutoffOpenedAt` and not
                // the `cutoffCreationDate` every lane above it uses. Sending the wrong name is silent:
                // Spring ignores an unknown query parameter and the lane re-serves page one forever.
                const cutoffOpenedAt = typeof paginationParameter === "string" ? paginationParameter : undefined;
                const response = await ClientFactory.getBoardsToVoteOnPage(cutoffOpenedAt);
                return response?.data ?? [];
            }

            // The three viewer-scoped lanes are sortable (audience-scopes design §4, HS-3a). The sort
            // ALWAYS travels: the endpoints serve `new` when `orderingStyle` is absent while the page's
            // default sort is `active`, so an omitted parameter would be a silent mismatch between the
            // pill and the list. The window travels only for the sort that takes one. Through the
            // wrappers' existing `options.params` seam, so the wrappers themselves are unchanged.
            const scopedParams = {
                params: {
                    orderingStyle,
                    ...(homeOrderingSupportsRange(orderingStyle) ? { range } : {}),
                },
            };

            if (feedType === "groups") {
                const groupsCursor = typeof paginationParameter === "string" ? paginationParameter : undefined;
                const response = await ClientFactory.getHomeYourGroupsFeed(groupsCursor, HOME_DISCUSSION_PAGE_SIZE, scopedParams);
                return toHomeGroupsFeedItems(response.data);
            }

            // The two audience scopes (audience-scopes design §9). Each MUST have its own branch: the
            // fallthrough below serves the blended home feed, so a scope without a branch would serve a
            // different feed under a correct-looking label. Same page size as every other branch —
            // `PaginatedListState` treats a short page as the end.
            if (feedType === "following") {
                const followingCursor = typeof paginationParameter === "string" ? paginationParameter : undefined;
                const response = await ClientFactory.getHomeFollowingFeed(followingCursor, HOME_DISCUSSION_PAGE_SIZE, scopedParams);
                return toHomeGroupsFeedItems(response.data);
            }

            if (feedType === "series") {
                const seriesCursor = typeof paginationParameter === "string" ? paginationParameter : undefined;
                const response = await ClientFactory.getHomeFollowedSeriesFeed(seriesCursor, HOME_DISCUSSION_PAGE_SIZE, scopedParams);
                return toHomeGroupsFeedItems(response.data);
            }

            const cursor = typeof paginationParameter === "string" ? paginationParameter : undefined;
            const response = await paginationApi.homeDiscussionFeed(cursor, HOME_DISCUSSION_PAGE_SIZE);
            return toHomeFeedListItems(response.data);
        } catch (e: unknown) {
            if (isInterruptedRequestError(e)) {
                throw e;
            }

            const error = e as { response?: { status?: number; data?: unknown }; message?: string };
            const status = error.response?.status;
            logger.error(`[HomeFeedList] fetchPage error status=${status} error=${error?.message ?? e}`, error?.response?.data ?? e);
            throw new Error(`Failed to fetch page: ${status ?? error?.message ?? e}`);
        }
    }, [feedType, orderingStyle, paginationApi, range]);

    // Feed discovery instrumentation (audience-scopes design §10, HS-2b). The followed-series scope is
    // `series-followed`, never `series`: that value already means the series PAGE's feed, and merging
    // two surfaces into one dimension makes both unreadable. The annotation is what keeps any other
    // future HomeFeedType value from silently becoming an unregistered GA4 dimension value.
    const analyticsFeedType: AnalyticsFeedType | null = feedType === "series" ? "series-followed" : feedType;
    const analyticsOrdering = React.useMemo(
        () => resolveHomeFeedOrdering(feedType, orderingStyle),
        [feedType, orderingStyle],
    );
    const handlePageShown = React.useCallback((info: {
        pageIndex: number;
        pageItems: DiscussionWithoutContentDTO[];
    }) => {
        if (analyticsFeedType === null) {
            return;
        }
        reportFeedPageShown({
            feedType: analyticsFeedType,
            ordering: analyticsOrdering,
            pageIndex: info.pageIndex,
            items: info.pageItems,
        });
    }, [analyticsFeedType, analyticsOrdering]);

    const getDiscussionId = React.useCallback((discussion: DiscussionWithoutContentDTO) => {
        const paginationKey = discussion?.paginationParameter;
        const normalizedPaginationKey = paginationKey == null ? undefined : String(paginationKey);
        return discussion?.publicId ?? discussion?.slug ?? normalizedPaginationKey;
    }, []);

    return (
        <div data-playwright-id="home-feed-list">
            {/* `HOME-FEED-10` (operator decision **D26**) — one honest line for the member whose graph
                is empty.

                Conditioned on `EMPTY` and on nothing else, which is what makes it temporary rather
                than permanent chrome: it disappears the first time the member follows anyone, joins
                anything or follows a series, with no dismissal to store and no state to keep. `SPARSE`
                deliberately does NOT get it — a member who follows two people HAS told us something,
                and telling them otherwise would be false.

                D26 fixes the phrasing policy and rule 12 binds it: one shared consequence phrasing, a
                promise about what will appear, never a description of how the feed is scored. The
                string says what following does, not what the ranker does with it.

                Rule 32: this is ADDITIVE and sits above the list without replacing it, so the island
                still renders everything the server-rendered HTML held.

                LCP: it can only appear for a SIGNED-IN member, because `viewerGraph` is null for an
                anonymous visitor. The CWV-measured cold path is the anonymous `/`, which is the render
                that carries `instantContent` and the inlined first page, and it is untouched. */}
            {viewerGraphTier === "EMPTY" ? (
                <div className={`${MAIN_PAGE_LIST_WIDTH_CLASS} mb-4`} data-playwright-id="home-starter-feed-notice">
                    <InfoBanner icon={UserPlus} description={localizer("home.starter-feed.notice")} />
                </div>
            ) : null}

            {/* SV-136 D3 (supersedes round-31 D10, which showed it on Following only): the inline
                short-post box, first on EVERY home tab and for a signed-in member only (A11). A post
                made from Groups or Series still goes to the author's followers (A12): no tab-specific
                behaviour. It sits ABOVE whatever the tab renders today, the empty state included, and
                adds nothing to that state's copy. Additive over the server HTML (rule 32): the SSR
                list never carried a composer, so nothing is replaced. */}
            {MetaHandler.getLoggedInUsername() ? (
                <div className={MAIN_PAGE_LIST_WIDTH_CLASS}>
                    <ShortPostComposer localizer={localizer} />
                    {/* Round 38 D16 (HOME-FEED-51) partly reverses A12 for this tab: still no group
                        target, but on a list where every post belongs to a group the box says where a
                        post from it goes. The Groups tab only; the other tabs already read as "yours". */}
                    {feedType === "groups" ? (
                        <p
                            className="mt-1 px-1 text-xs leading-4 text-muted-foreground"
                            data-playwright-id="home-groups-composer-audience"
                        >
                            {localizer("home.feed.groups_tab.audience")}
                        </p>
                    ) : null}
                </div>
            ) : null}

            <PaginatedListWrapperV2<DiscussionWithoutContentDTO, string | number>
                key={`${feedType}-${orderingStyle}-${range}`}
                // Back-navigation restoration (SV-24). Mirrors `key=` on purpose: the two must change
                // together, or a remount triggered by a feed/ordering/range switch would restore the
                // PREVIOUS selection's pages into the new one. The history entry supplies the rest of the
                // key, so this only has to be unique within a page. Deleting this one prop is the entire
                // rollback for the feature on this surface.
                restorationKey={`home:${feedType}-${orderingStyle}-${range}`}
                fetchPage={fetchPage}
                // Native resume (round 12 slice 1m, D11/D28): silent swap at the top, "New posts" pill below.
                nativeResumeRefresh={nativeResumeNewPosts}
                // EMPTY-STATES-26. The predicate is one-sided — passing a value LARGER than the server's
                // page would end the list early — so this is safe only if EVERY branch of `fetchPage`
                // serves at least this many. Verified 2026-08-21, all eight: `active`, `new`,
                // `controversial`, `arguments`, `agreed`, `unanswered` and `boards` are hardcoded 20
                // in `PaginationService`; `groups` and the default `homeDiscussionFeed` honour this exact
                // value through `normalizePageSize`. Minimum across the set is 10, so 10 is safe.
                // ⚠️ Six of those branches pass NO page size at all, so their size lives only in
                // `PaginationService`. Re-verify this list before changing this constant or adding a
                // branch — nothing here fails at compile time.
                pageSize={HOME_DISCUSSION_PAGE_SIZE}
                initialItems={initialFeedItems}
                onPageShown={handlePageShown}
                getId={getDiscussionId}
                className={MAIN_PAGE_LIST_WIDTH_CLASS}
                statusSlots={createStandardPaginatedStatusSlots({
                    initialLoading: <DiscussionCardListSkeleton className="gap-0" />,
                    loadingMessage: localizer("home.loading"),
                    endMessage: localizer("home.end-of-list"),
                    // `HOME-FEED-19` / `DASHBOARD-32` / `EMPTY-STATES-60`. A terminator below a list
                    // that never paginated is pagination machinery, which rule 12 keeps out of
                    // user-facing copy, and it can read as "the list was cut short". `?feed=groups`
                    // routinely ends at two cards. Seven sibling call sites already passed this
                    // (`MyDashboardPage`, six on `UserPage`); this was the eighth consumer and the only
                    // one without it.
                    //
                    // Same one-sided safety argument as `pageSize` above: passing a value LARGER than
                    // the server page would hide a legitimate terminator, so this must not exceed the
                    // smallest page any `fetchPage` branch serves — which is why it reuses that exact
                    // constant rather than a number of its own.
                    endMinItems: HOME_DISCUSSION_PAGE_SIZE,
                    errorMessage: localizer("paginated.load_failed"),
                    retryLabel: localizer("literal.retry"),
                    // home.jte emits crawlableFeed as real anchors behind this island.
                    ssrFallback: crawlableDiscussionListFallback("HomePage"),
                    // `HOME-FEED-11` — "your filter emptied this", not "the site is empty".
                    //
                    // The container selects `filtered` over `empty` when `isFiltered` is true, but it
                    // cannot detect the condition itself: the HOST owns the controls, so the host has to
                    // pass the predicate. Without this an all-site feed narrowed to Past 24h on a quiet
                    // day told the member the SITE had nothing, in first-run words ("yet"), with no
                    // action at all — the `groups`/`home` action branch below does not apply to
                    // `feed=all`.
                    //
                    // ⚠️ The predicate is `homeSelectionSupportsRange` restated, NOT `range !== "all"`.
                    // `range` can outlive the sort that gave it meaning: a member who picks Past 24h and
                    // then switches to Newest still carries `range === "day"` in state while the range
                    // pill is not rendered at all (a scope normalizes it away, Everywhere keeps it). Offering "Clear filters" there would name a filter
                    // the member cannot see and which is narrowing nothing. This is true exactly when a
                    // VISIBLE control is narrowing the list — which is also what `EMPTY-STATES-33`'s
                    // closure means by "the predicate has to be real, never a constant false".
                    isFiltered: homeFeedTypeSupportsSort(feedType) && homeOrderingSupportsRange(orderingStyle) && range !== "all",
                    filtered: onRangeChange ? (
                        <PaginatedListEmptyState
                            variant="filtered"
                            icon={FilterX}
                            title={localizer("paginated.filtered.title")}
                            description={localizer("paginated.filtered.description")}
                            action={{
                                /*
                                 * Tier-2 finding 29 — ONE rung wider, not straight to all time.
                                 * Jumping to `all` threw away every window between, so a member who
                                 * narrowed to "Past 24h" could not ask for the week. The label follows
                                 * the behaviour: this widens, it no longer clears.
                                 */
                                label: localizer("home.range.widen"),
                                icon: CalendarRange,
                                onClick: () => onRangeChange(nextWiderRange(range)),
                            }}
                        />
                    ) : undefined,
                    empty: (
                        <PaginatedListEmptyState
                            icon={MessageSquareText}
                            title={localizer("home.no-discussions")}
                            // The board lane's emptiness is an ACHIEVEMENT rather than a shortage —
                            // the member has voted on every open board — so the generic "nothing here
                            // yet" description would read as the site being empty on the one lane
                            // where it means the opposite. Only the description changes: the shared
                            // title stays, because re-titling would have cost a key nobody authored
                            // and the sentence below already carries the whole meaning.
                            description={feedType === "boards"
                                ? localizer("discussion.board.lane.waiting.empty")
                                // Tenet T6: say what belongs here, how to put something here, and
                                // offer the one action that does it (audience-scopes design §11).
                                : feedType === "following"
                                    ? localizer("home.feed-empty.following")
                                    : feedType === "series"
                                        ? localizer("home.feed-empty.series")
                                        : localizer("paginated.empty.description")}
                            action={feedType === "following" ? {
                                // The search page's typed tab is the only people / series destination
                                // in the product (§11; verified against SearchController.VALID_SEARCH_TYPES).
                                label: localizer("home.feed-empty.following.action"),
                                icon: Compass,
                                onClick: () => { navigateToUrl("/search?searchType=users", { source: "home_feed_following_empty_state" }); },
                            } : feedType === "series" ? {
                                label: localizer("home.feed-empty.series.action"),
                                icon: Compass,
                                onClick: () => { navigateToUrl("/search?searchType=series", { source: "home_feed_series_empty_state" }); },
                            } : feedType === "groups" ? {
                                // `groups.directory.title`, not the retired `groups.directory.discover`:
                                // the two held byte-identical values in all 25 catalogs, and the
                                // duplicate was deleted on 2026-08-17 with `DISCOVERY-28` (the same
                                // string printed twice, three elements apart, on `/groups` itself).
                                label: localizer("groups.directory.title"),
                                icon: Compass,
                                // Through the navigation seam, never a raw `window.location.assign`
                                // (review FR-F4 / FQ-F6). `/groups` is soft-navigation-eligible, and
                                // `navigateToUrl` is what dispatches the cancelable
                                // `seavien:navigation-intent` the three route listeners consume; a raw
                                // assign dispatches nothing and tears the shell down to reach a client
                                // route. It also carries the unsafe-scheme and unresolved-path-segment
                                // guards, and is overridable in tests.
                                onClick: () => { navigateToUrl("/groups", { source: "home_feed_groups_empty_state" }); },
                            } : feedType === "home" && onFeedTypeChange ? {
                                label: localizer("home.no-discussions.browse-all"),
                                icon: Compass,
                                onClick: () => onFeedTypeChange("all"),
                            } : undefined}
                        />
                    ),
                })}
                render={({ items, phase }) => {
                    const pages = chunkFeedItems(items, inlineDiscoveryLead, inlineDiscoveryInterval);
                    const adsAllowed = areAdsAllowed();
                    let insertedDiscoveryRows = 0;
                    let insertedAdSlots = 0;
                    let fullBoundaryIndex = 0;

                    // `HOME-FEED-8`. Below `lg` the desktop rail is gone (`hidden lg:block`) and the ONLY
                    // discovery the screen has is these interleaved rows — which are emitted on a full
                    // chunk and nothing else. So a feed that ends short of one interval gets no discovery
                    // layer at all, at any scroll depth: `?feed=groups` routinely ends at two cards, well
                    // short of the non-`home` interval. The phone simply stops where the desktop hands the
                    // same member three ways forward from data that already loaded.
                    //
                    // `phase === "end"` is the CONTAINER's own definition of exhausted
                    // (`hasVisibleItems && !hasMore`), so this does not restate a predicate that could drift
                    // from it. It is deliberately NOT `endMinItems`-aware: that constant suppresses the
                    // terminator *text* below a list that never paginated (`HOME-FEED-19`), and a list too
                    // short to earn the word "End of list" is exactly the list that most needs a way out.
                    const feedIsExhausted = phase === "end";
                    // Audience-scopes design §11 (HS-2b; `HOME-FEED-19`'s open half). A personal scope
                    // that is over INSIDE ONE PAGE is functionally empty from the member's side, so it
                    // earns the empty state's own recovery action, and the terminal slot holds that ONE
                    // thing: not the discovery module, not "Browse all". "Never paginated" is the same
                    // predicate `endMinItems` uses to withhold the terminator (`items.length <
                    // HOME_DISCUSSION_PAGE_SIZE`), so the two cannot drift. `groups` is deliberately
                    // absent: D25 (`HOME-FEED-27`) gave that slot to the member's own groups, and which
                    // of the two a thin groups feed shows is parked (`r4w2-home-HS-2b-groups.md`).
                    const thinScopeRecovery = feedIsExhausted && items.length < HOME_DISCUSSION_PAGE_SIZE
                        ? (feedType === "following" ? {
                            label: localizer("home.feed-empty.following.action"),
                            onClick: () => { navigateToUrl("/search?searchType=users", { source: "home_feed_following_thin_state" }); },
                        } : feedType === "series" ? {
                            label: localizer("home.feed-empty.series.action"),
                            onClick: () => { navigateToUrl("/search?searchType=series", { source: "home_feed_series_thin_state" }); },
                        } : null)
                        : null;
                    // `HOME-FEED-27` (**D25**) rides `HOME-FEED-8`'s slot rather than asking for one of
                    // its own — "coordinate, do not duplicate". On the `groups` feed the member's own
                    // groups ARE the thing the screen has left to show; anywhere else the rotation head
                    // is, exactly as before.
                    const terminalDiscoveryRow = yourGroupsRow ?? inlineDiscoveryRows[0] ?? null;

                    const rangeIsNarrowed = homeFeedTypeSupportsSort(feedType)
                        && homeOrderingSupportsRange(orderingStyle)
                        && range !== "all";
                    const renderEndOfFeedAction = () => {
                        if (rangeIsNarrowed && onRangeChange) {
                            return (
                                <div className="flex justify-center pb-2" data-playwright-id="home-end-of-feed-action">
                                    <Button variant="outline" className="rounded-full" onClick={() => onRangeChange(nextWiderRange(range))}>
                                        <CalendarRange className="size-4" aria-hidden="true" />
                                        {localizer("home.range.widen")}
                                    </Button>
                                </div>
                            );
                        }

                        if (feedType !== "all" && onFeedTypeChange) {
                            return (
                                <div className="flex justify-center pb-2" data-playwright-id="home-end-of-feed-action">
                                    <Button variant="outline" className="rounded-full" onClick={() => onFeedTypeChange("all")}>
                                        <Compass className="size-4" aria-hidden="true" />
                                        {localizer("home.no-discussions.browse-all")}
                                    </Button>
                                </div>
                            );
                        }

                        // Already on the whole corpus, all time. There is honestly nothing to widen, and
                        // inventing a control that does nothing is worse than the plain terminator.
                        return null;
                    };

                    return (
                        // SV-135 S2 (design §5.2): rows sit flush, split only by each row's own
                        // `--divider` edge, so the stack has no gap. The discovery/ad boundary nodes and
                        // the recovery pill are not rows: they keep their breathing room as padding.
                        <MainPageListStack className="gap-0" data-playwright-id="home-feed-items">
                            {pages.map((page, pageIndex) => {
                                const isFullBoundary = page.items.length === page.expectedSize;

                                // On each full-page boundary we surface a discovery row. When ads are
                                // enabled+consented, every other boundary is diverted to an in-feed ad
                                // instead, so the two never stack and the feed stays light (the first ad
                                // lands after ~2 pages, comfortably below the fold). When ads are off,
                                // every boundary stays a discovery row (unchanged behavior).
                                let boundaryNode: React.ReactNode = null;
                                if (isFullBoundary) {
                                    const showAd = adsAllowed && fullBoundaryIndex % 2 === 1;
                                    fullBoundaryIndex += 1;

                                    if (showAd) {
                                        boundaryNode = (
                                            <AdSlot
                                                key={`home-infeed-${insertedAdSlots}`}
                                                placement="home-infeed"
                                                index={insertedAdSlots}
                                                localizer={localizer}
                                            />
                                        );
                                        insertedAdSlots += 1;
                                    } else if (inlineDiscoveryRows.length > 0) {
                                        const discoveryRow = inlineDiscoveryRows[insertedDiscoveryRows % inlineDiscoveryRows.length];
                                        insertedDiscoveryRows += 1;
                                        const { key: discoveryRowKey, ...discoveryRowProps } = discoveryRow;
                                        boundaryNode = (
                                            <HomeInlineDiscoveryRow
                                                key={`discovery-row-${pageIndex}-${discoveryRowKey}`}
                                                {...discoveryRowProps}
                                                localizer={localizer}
                                                onRetry={onRetry}
                                            />
                                        );
                                    }
                                }

                                return (
                                    <React.Fragment key={`discussion-page-${pageIndex}`}>
                                        {page.items.map((discussion, discussionIndex) => (
                                            <DiscussionCardDisplayer
                                                key={getDiscussionId(discussion) ?? `discussion-${pageIndex}-${discussionIndex}`}
                                                discussion={discussion}
                                                viewerIsLeaderOfGroup={false}
                                                // The very first feed card is the above-the-fold LCP
                                                // candidate: load its image eagerly at high priority.
                                                priorityImage={pageIndex === 0 && discussionIndex === 0}
                                                feedAttribution={analyticsFeedType === null ? undefined : {
                                                    feedType: analyticsFeedType,
                                                    ordering: analyticsOrdering,
                                                    // `pages` are the client-side discovery-row chunks,
                                                    // not network pages, so the slot has to be
                                                    // re-derived rather than read off discussionIndex.
                                                    // Read off the chunk's own `startIndex` since
                                                    // `HOME-FEED-9`: the first chunk can be shorter
                                                    // than the rest, so `pageIndex * interval` would
                                                    // over-count every position after it.
                                                    position: page.startIndex + discussionIndex + 1,
                                                    // Already computed by getHomeFeedSql(), already on
                                                    // the DTO, and until now thrown away in the browser.
                                                    recommendationSource: discussion.recommendationSource,
                                                }}
                                                localizer={localizer}
                                            />
                                        ))}

                                        {boundaryNode ? <div className="py-3">{boundaryNode}</div> : null}
                                    </React.Fragment>
                                );
                            })}

                            {/* `HOME-FEED-8` — the terminal placement.
                                JSX children evaluate in order, so `insertedDiscoveryRows` here is the final
                                count from the boundary pass above. Gated on ZERO: a feed that already got an
                                interleaved row does not need a second one at the foot, and stacking two is
                                exactly what `HOME-FEED-19` warns the terminal slot must not do.
                                Terminal rather than interleaved on purpose — the constraint recorded at
                                `HOME-FEED-9` is that the first feed card is the LCP element, so nothing may
                                be lifted above it or reserve height it might not fill. Below the last card
                                it can do neither. */}
                            {thinScopeRecovery ? (
                                <div className="flex justify-center pt-3 pb-2" data-playwright-id="home-thin-feed-recovery">
                                    <Button variant="outline" className="rounded-full" onClick={thinScopeRecovery.onClick}>
                                        <Compass className="size-4" aria-hidden="true" />
                                        {thinScopeRecovery.label}
                                    </Button>
                                </div>
                            ) : null}

                            {!thinScopeRecovery && feedIsExhausted && insertedDiscoveryRows === 0 && terminalDiscoveryRow ? (() => {
                                const { key: terminalRowKey, ...terminalRowProps } = terminalDiscoveryRow;
                                return (
                                    <HomeInlineDiscoveryRow
                                        key={`discovery-row-end-${terminalRowKey}`}
                                        {...terminalRowProps}
                                        localizer={localizer}
                                        onRetry={onRetry}
                                    />
                                );
                            })() : null}

                            {/* `HOME-FEED-8`'s complement, and tier-1 finding 14 (2026-09-01).
                                The row above is gated on `insertedDiscoveryRows === 0`, so it serves the
                                SHORT feed. The deep reader -- the one who crossed several full-page
                                boundaries and therefore has interleaved rows -- reached the bottom and got
                                one line of grey text, while the member with an EMPTY feed and the member
                                whose filter matched nothing both get a panel with an action. The reader who
                                went furthest was the only one offered nothing.

                                Complementary rather than stacked, which is what `HOME-FEED-19` warns the
                                terminal slot must not do: this renders exactly when the discovery row did
                                not. Zero new copy -- `home.no-discussions.browse-all` and
                                `paginated.filtered.clear` are the same acts the empty and filtered slots
                                already offer, and both are already in all 25 catalogs.

                                Order matters: a narrowed RANGE is the more likely reason a feed the member
                                chose ran out, and widening it keeps them where they are, so it is offered
                                ahead of leaving the feed entirely. */}
                            {!thinScopeRecovery && feedIsExhausted && !(insertedDiscoveryRows === 0 && terminalDiscoveryRow)
                                ? renderEndOfFeedAction()
                                : null}
                        </MainPageListStack>
                    );
                }}
            />
        </div>
    );
}
